import { supabase } from '@/lib/supabase';
import { Platform } from 'react-native';

let ImagePicker: any;
let ImageManipulator: any;

if (Platform.OS !== 'web') {
  ImagePicker = require('expo-image-picker');
  ImageManipulator = require('expo-image-manipulator');
}

export interface ProfilePictureUploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export interface ImageModerationResult {
  isValid: boolean;
  reason?: string;
}

const MAX_IMAGE_SIZE = 400;
const JPEG_QUALITY = 0.8;

export async function requestMediaLibraryPermissions(): Promise<boolean> {
  if (Platform.OS === 'web' || Platform.OS === 'android') {
    return true;
  }

  const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
  return status === 'granted';
}

export async function requestCameraPermissions(): Promise<boolean> {
  if (Platform.OS === 'web') {
    return true;
  }

  const { status } = await ImagePicker.requestCameraPermissionsAsync();
  return status === 'granted';
}

export async function pickImageFromLibrary(): Promise<any> {
  try {
    if (Platform.OS === 'web') {
      return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e: any) => {
          const file = e.target.files[0];
          if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({
                uri: reader.result as string,
                width: 0,
                height: 0,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          } else {
            resolve(null);
          }
        };
        input.click();
      });
    }

    const hasPermission = await requestMediaLibraryPermissions();
    if (!hasPermission) {
      throw new Error('Media library permission denied');
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 1,
    });

    if (result.canceled) {
      return null;
    }

    return result.assets[0];
  } catch (error) {
    console.error('[Profile Picture] Error picking image:', error);
    throw error;
  }
}

export async function takePhotoWithCamera(): Promise<any> {
  try {
    if (Platform.OS === 'web') {
      return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        (input as any).capture = 'environment';
        input.onchange = (e: any) => {
          const file = e.target.files[0];
          if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({
                uri: reader.result as string,
                width: 0,
                height: 0,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          } else {
            resolve(null);
          }
        };
        input.click();
      });
    }

    const hasPermission = await requestCameraPermissions();
    if (!hasPermission) {
      throw new Error('Camera permission denied');
    }

    const result = await ImagePicker.launchCameraAsync({
      allowsEditing: true,
      aspect: [1, 1],
      quality: 1,
    });

    if (result.canceled) {
      return null;
    }

    return result.assets[0];
  } catch (error) {
    console.error('[Profile Picture] Error taking photo:', error);
    throw error;
  }
}

export async function resizeAndCompressImage(uri: string): Promise<string> {
  try {
    console.log('[Profile Picture] Resizing and compressing image:', uri);

    if (Platform.OS === 'web') {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_IMAGE_SIZE) {
              height *= MAX_IMAGE_SIZE / width;
              width = MAX_IMAGE_SIZE;
            }
          } else {
            if (height > MAX_IMAGE_SIZE) {
              width *= MAX_IMAGE_SIZE / height;
              height = MAX_IMAGE_SIZE;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
        };
        img.onerror = reject;
        img.src = uri;
      });
    }

    const manipResult = await ImageManipulator.manipulateAsync(
      uri,
      [{ resize: { width: MAX_IMAGE_SIZE, height: MAX_IMAGE_SIZE } }],
      { compress: JPEG_QUALITY, format: ImageManipulator.SaveFormat.JPEG }
    );

    console.log('[Profile Picture] Image compressed:', manipResult.uri);
    return manipResult.uri;
  } catch (error) {
    console.error('[Profile Picture] Error resizing image:', error);
    throw error;
  }
}

export async function convertImageToBase64(uri: string): Promise<string> {
  try {
    const response = await fetch(uri);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('[Profile Picture] Error converting to base64:', error);
    throw error;
  }
}

export async function moderateImage(imageBase64: string): Promise<ImageModerationResult> {
  try {
    console.log('[Profile Picture] Moderating image with OpenAI');

    const response = await fetch(
      `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/moderate-profile-picture`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ imageBase64 }),
      }
    );

    if (!response.ok) {
      console.error('[Profile Picture] Moderation API error:', response.statusText);
      return {
        isValid: true,
        reason: 'Moderation service unavailable',
      };
    }

    const result = await response.json();
    console.log('[Profile Picture] Moderation result:', result);
    return result;
  } catch (error) {
    console.error('[Profile Picture] Error moderating image:', error);
    return {
      isValid: true,
      reason: 'Moderation service error',
    };
  }
}

async function convertUriToBlob(uri: string): Promise<Blob> {
  if (Platform.OS === 'web') {
    const response = await fetch(uri);
    return await response.blob();
  }

  const response = await fetch(uri);
  const blob = await response.blob();
  return blob;
}

export async function uploadProfilePicture(
  userId: string,
  imageUri: string,
  authToken?: string
): Promise<ProfilePictureUploadResult> {
  try {
    console.log('[Profile Picture] Starting upload process for user:', userId);

    const compressedUri = await resizeAndCompressImage(imageUri);
    const base64Data = await convertImageToBase64(compressedUri);

    const moderationResult = await moderateImage(base64Data);
    if (!moderationResult.isValid) {
      console.log('[Profile Picture] Image rejected by moderation:', moderationResult.reason);
      return {
        success: false,
        error: moderationResult.reason || 'Image contains inappropriate content',
      };
    }

    // If authToken is provided, use edge function for secure upload
    if (authToken) {
      console.log('[Profile Picture] Uploading via edge function');

      const response = await fetch(
        `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/upload-profile-picture`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            smartuserId: userId,
            imageBase64: `data:image/jpeg;base64,${base64Data}`,
            authToken: authToken,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[Profile Picture] Edge function error:', errorData);
        return {
          success: false,
          error: errorData.error || 'Failed to upload profile picture',
        };
      }

      const result = await response.json();
      console.log('[Profile Picture] Edge function upload successful:', result.url);

      return {
        success: result.success,
        url: result.url,
        error: result.error,
      };
    }

    // Fallback to direct upload (for backward compatibility, though this will fail with current RLS)
    console.log('[Profile Picture] Attempting direct upload (legacy method)');
    const fileName = `${userId}/${Date.now()}.jpg`;
    console.log('[Profile Picture] Uploading to storage:', fileName);

    const blob = await convertUriToBlob(compressedUri);

    const { data, error } = await supabase.storage
      .from('profile-pictures')
      .upload(fileName, blob, {
        contentType: 'image/jpeg',
        upsert: false,
      });

    if (error) {
      console.error('[Profile Picture] Upload error:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    const { data: publicUrlData } = supabase.storage
      .from('profile-pictures')
      .getPublicUrl(fileName);

    console.log('[Profile Picture] Upload successful:', publicUrlData.publicUrl);

    return {
      success: true,
      url: publicUrlData.publicUrl,
    };
  } catch (error) {
    console.error('[Profile Picture] Error uploading:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export async function deleteOldProfilePicture(profilePictureUrl: string): Promise<void> {
  try {
    if (!profilePictureUrl) return;

    // Note: When using the edge function for uploads, old pictures are deleted automatically
    // This function is kept for backward compatibility and manual cleanup

    const url = new URL(profilePictureUrl);
    const pathParts = url.pathname.split('/profile-pictures/');
    if (pathParts.length < 2) return;

    const filePath = pathParts[1];
    console.log('[Profile Picture] Deleting old image:', filePath);

    const { error } = await supabase.storage
      .from('profile-pictures')
      .remove([filePath]);

    if (error) {
      console.error('[Profile Picture] Error deleting old image:', error);
    } else {
      console.log('[Profile Picture] Old image deleted successfully');
    }
  } catch (error) {
    console.error('[Profile Picture] Error in deleteOldProfilePicture:', error);
  }
}

export async function updateUserProfilePicture(
  smartuserId: number,
  profilePictureUrl: string
): Promise<boolean> {
  try {
    // Note: When using the edge function for uploads, the database is updated automatically
    // This function is kept for backward compatibility

    console.log('[Profile Picture] Updating user profile picture in database');

    const { error } = await supabase
      .from('users')
      .update({
        profile_picture_url: profilePictureUrl,
        updated_at: new Date().toISOString(),
      })
      .eq('smartuser_id', smartuserId);

    if (error) {
      console.error('[Profile Picture] Database update error:', error);
      return false;
    }

    console.log('[Profile Picture] Database updated successfully');
    return true;
  } catch (error) {
    console.error('[Profile Picture] Error updating database:', error);
    return false;
  }
}

export async function removeProfilePicture(smartuserId: number): Promise<boolean> {
  try {
    console.log('[Profile Picture] Removing profile picture for user:', smartuserId);

    const { data: userData, error: fetchError } = await supabase
      .from('users')
      .select('profile_picture_url')
      .eq('smartuser_id', smartuserId)
      .single();

    if (fetchError) {
      console.error('[Profile Picture] Error fetching user data:', fetchError);
      return false;
    }

    if (userData?.profile_picture_url) {
      await deleteOldProfilePicture(userData.profile_picture_url);
    }

    const { error: updateError } = await supabase
      .from('users')
      .update({
        profile_picture_url: null,
        updated_at: new Date().toISOString(),
      })
      .eq('smartuser_id', smartuserId);

    if (updateError) {
      console.error('[Profile Picture] Error updating database:', updateError);
      return false;
    }

    console.log('[Profile Picture] Profile picture removed successfully');
    return true;
  } catch (error) {
    console.error('[Profile Picture] Error removing profile picture:', error);
    return false;
  }
}
